import styled from "styled-components";
import { useState, useEffect } from "react";
import { JSONSchemaTypeOption } from "../utils/BlueprintDataTransformUtils";
import Dropdown from "../right-panel/Dropdown";
import { Button, ButtonVariant } from "@merge-api/merge-javascript-shared";
import { validateKeyName } from "./BlueprintValidationEditorUtils";

const FieldName = styled.div`
  display: flex;
  font-weight: 700;
  font-size: 13px;
  font-family: var(--font-family-monospace);
`;

const FieldInputErrorText = styled.div`
  display: flex;
  font-size: 11px;
  color: red;
`;

const FieldInputHelpText = styled.div`
  display: flex-row;
  font-size: 11px;
  padding: 5px;
  padding-left: 0;
  padding-top: 10px;
`;

const HelpTextList = styled.ul`
  padding: 0;
  padding-top: 5px;
`;

interface Props {
  addField: (fieldKey: string, fieldType: JSONSchemaTypeOption) => void;
  level: number;
}

const BlueprintValidationEditorField = ({ addField }: Props) => {
  const [fieldKey, setFieldKey] = useState<string>("");
  const [fieldType, setFieldType] = useState<JSONSchemaTypeOption>();
  const [isEditing, setIsEditing] = useState(false);
  const [isKeyNameValid, setIsKeyNameValid] = useState(false);

  useEffect(() => {
    setIsKeyNameValid(validateKeyName(fieldKey));
  }, [fieldKey]);

  const isDisabled = !fieldType || !fieldKey || fieldKey === "" || !isKeyNameValid;

  return (
    <div className="pl-20 py-4 flex flex-auto justify-normal w-auto max-w-full gap-3">
      {!isEditing ? (
        <Button onClick={() => setIsEditing(true)}>Add new field</Button>
      ) : (
        <>
          <div className="flex flex-col">
            <FieldName>New field</FieldName>
            <FieldInputHelpText className="text-gray-800">
              <u>GUIDELINE FOR NAMING</u>
              <br />
              <HelpTextList>
                <li>Only add if truly needed.</li>
                <li>
                  If Merge ID, name as <b>XXX_merge_id</b>
                </li>
                <li>
                  If remote ID, name as <b>XXX_remote_id</b>
                </li>
                <li>
                  If date, name as <b>remote_XXX_at</b>
                </li>
                <li>
                  If field is part of another object "YYY", name as <b>YYY_XXX</b>
                </li>
              </HelpTextList>
              See full guideline{" "}
              <a
                href="https://www.notion.so/mergeapi/Naming-guidelines-for-integration_params-26cc9aaeb6064a72a36fd77080d4fe05"
                target="_blank"
              >
                here
              </a>
              .
            </FieldInputHelpText>
          </div>
          <div className="flex flex-col">
            <input
              className="form-control mr-6"
              onChange={(e) => setFieldKey(e.target.value)}
              placeholder="Field Key"
              value={fieldKey}
              autoComplete="no"
            />
            {!isKeyNameValid ? (
              <FieldInputErrorText>
                Use lowercase only.
                <br />
                Use underscore, not spaces.
              </FieldInputErrorText>
            ) : (
              <></>
            )}
          </div>
          <div className="flex flex-col">
            <Dropdown
              choices={Object.values(JSONSchemaTypeOption).map((option) => ({
                name: option,
                id: option,
              }))}
              currentValue={fieldType}
              placeholder={"Field Type"}
              onChange={(e) => setFieldType(e.target.value)}
            />
          </div>
          <div className="flex flex-row gap-3">
            <Button
              disabled={isDisabled}
              onClick={() => {
                if (fieldType) {
                  addField(fieldKey, fieldType);
                  setIsEditing(false);
                  setFieldKey("");
                  setFieldType(undefined);
                }
              }}
            >
              <b>Create</b>
            </Button>
            <Button variant={ButtonVariant.SecondaryGray} onClick={() => setIsEditing(false)}>
              <b>Cancel</b>
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

export default BlueprintValidationEditorField;
